import React from 'react';
import { TextStyleFields, TextSizePreset } from './types';
import { CAPTION_FONTS, FONT_PCT_MAX, FONT_PCT_MIN, FONT_PCT_STEP, TEXT_SIZE_PRESETS } from './constants';
import { resolveTextStyle } from './textStyle';

export default function TextStyleToolbar({ fields, onChange }: { fields: TextStyleFields; onChange: (patch: Partial<TextStyleFields>) => void }) {
  const style = resolveTextStyle(fields);
  const clampPct = (v: number) => Math.min(FONT_PCT_MAX, Math.max(FONT_PCT_MIN, +v.toFixed(1)));
  const bumpSize = (delta: number) => {
    onChange({ fontSizePct: clampPct(style.fontSizePct + delta), sizePreset: 'custom' });
  };
  return (
    <div className="se-text-style-toolbar">
      <div className="se-select-wrap se-style-size-wrap">
        <select
          className="se-style-select"
          value={style.sizePreset}
          onChange={e => {
            const sizePreset = e.target.value as TextSizePreset;
            const presetDef = TEXT_SIZE_PRESETS.find(p => p.value === sizePreset);
            onChange({
              sizePreset,
              fontSizePct: sizePreset === 'custom' ? style.fontSizePct : (presetDef?.pct ?? style.fontSizePct),
            });
          }}
          title="Text size style"
        >
          {TEXT_SIZE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>
      <span className="se-style-divider" />
      <div className="se-select-wrap se-style-font-wrap">
        <select
          className="se-style-select"
          value={style.fontFamily}
          onChange={e => onChange({ fontFamily: e.target.value })}
          title="Font"
          style={{ fontFamily: `'${style.fontFamily}', sans-serif` }}
        >
          {CAPTION_FONTS.map(f => (
            <option key={f} value={f} style={{ fontFamily: `'${f}', sans-serif` }}>{f}</option>
          ))}
        </select>
      </div>
      <span className="se-style-divider" />
      <div className="se-size-stepper">
        <button type="button" onClick={() => bumpSize(-FONT_PCT_STEP)} title="Decrease size">−</button>
        <input
          type="number"
          value={style.fontSizePct}
          min={FONT_PCT_MIN}
          max={FONT_PCT_MAX}
          step={FONT_PCT_STEP}
          onChange={e => {
            const next = Number(e.target.value);
            if (Number.isFinite(next)) onChange({ fontSizePct: clampPct(next), sizePreset: 'custom' });
          }}
          title="Text size, as a percentage of video height"
        />
        <span className="se-unit-pct">%</span>
        <button type="button" onClick={() => bumpSize(FONT_PCT_STEP)} title="Increase size">+</button>
      </div>
      <span className="se-style-divider" />
      <button
        type="button"
        className={`se-style-icon-btn ${style.bold ? 'se-format-active' : ''}`}
        onClick={() => onChange({ bold: !style.bold })}
        title="Bold"
      ><b>B</b></button>
      <button
        type="button"
        className={`se-style-icon-btn ${style.italic ? 'se-format-active' : ''}`}
        onClick={() => onChange({ italic: !style.italic })}
        title="Italic"
      ><i>I</i></button>
      <button
        type="button"
        className={`se-style-icon-btn ${style.underline ? 'se-format-active' : ''}`}
        onClick={() => onChange({ underline: !style.underline })}
        title="Underline"
      ><u>U</u></button>
      <span className="se-style-divider" />
      <label className="se-style-icon-btn se-color-swatch" title={`Text color · ${style.color}`}>
        <span className="se-color-swatch-glyph">
          A
          <span className="se-color-swatch-bar" style={{ background: style.color }} />
        </span>
        <input type="color" value={style.color} onChange={e => onChange({ color: e.target.value })} />
      </label>
      <label className="se-style-icon-btn se-color-swatch" title={`Background color · ${style.bgColor ?? 'default'}`}>
        <span className="se-color-swatch-fill" style={{ background: style.bgColor ?? 'rgba(0,0,0,0.5)' }} />
        <input type="color" value={style.bgColor ?? '#000000'} onChange={e => onChange({ bgColor: e.target.value })} />
        {style.bgColor && (
          <button
            type="button"
            className="se-color-reset-badge"
            onClick={e => { e.preventDefault(); e.stopPropagation(); onChange({ bgColor: null }); }}
            title="Reset background to default"
          >×</button>
        )}
      </label>
    </div>
  );
}
